/**
 * Collects decisions from storyflow graphs for use in the onboarding quiz.
 *
 * In standalone mode the Decisions API is unavailable, so we extract
 * decisions directly from each chapter's graph (same source of truth
 * the admin API syncs from). Returns decisions grouped + sorted by
 * chapter number in graph traversal order for a natural quiz flow.
 */

function extractDecisionsFromGraph(storyflow) {
  const nodes = storyflow?.graph?.nodes || [];
  const edges = storyflow?.graph?.edges || [];
  const decisionNodes = nodes.filter(
    (n) => n.type === 'sfDecision' && n.data?.decisionName
  );
  if (decisionNodes.length <= 1) {
    return decisionNodes.map((n) => ({
      nodeId: n.id,
      storyflowId: storyflow.id,
      chapterNumber: storyflow.number ?? 0,
      chapterName: storyflow.name || '',
      decisionName: n.data.decisionName,
      options: n.data.options || []
    }));
  }

  // Walk the graph from sfStart to approximate in-play order. Fall
  // back to the raw node array for any decisions never reached (dead
  // code branches still belong in the quiz).
  const startNode = nodes.find((n) => n.type === 'sfStart') || nodes[0];
  const visited = new Set();
  const ordered = [];
  const stack = startNode ? [startNode.id] : [];
  while (stack.length) {
    const id = stack.pop();
    if (visited.has(id)) continue;
    visited.add(id);
    const node = nodes.find((n) => n.id === id);
    if (!node) continue;
    if (node.type === 'sfDecision' && node.data?.decisionName) {
      ordered.push(node);
    }
    // Push all outgoing edges — reverse so earlier handles are popped first
    const outs = edges.filter((e) => e.source === id);
    for (let i = outs.length - 1; i >= 0; i--) {
      if (!visited.has(outs[i].target)) stack.push(outs[i].target);
    }
  }
  const orderedIds = new Set(ordered.map((n) => n.id));
  for (const n of decisionNodes) {
    if (!orderedIds.has(n.id)) ordered.push(n);
  }

  return ordered.map((n) => ({
    nodeId: n.id,
    storyflowId: storyflow.id,
    chapterNumber: storyflow.number ?? 0,
    chapterName: storyflow.name || '',
    decisionName: n.data.decisionName,
    options: n.data.options || []
  }));
}

/**
 * All decisions for chapters strictly before the target chapter number.
 * Chapter 0 (backstory) is included — the user configures it in the quiz.
 */
export function collectDecisionsBefore(allStoryflows, targetChapterNumber) {
  if (!allStoryflows?.length) return [];
  const chapters = [...allStoryflows]
    .filter((s) => (s.number ?? null) !== null && (s.number ?? 0) < targetChapterNumber)
    .sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
  const out = [];
  for (const sf of chapters) out.push(...extractDecisionsFromGraph(sf));
  return out;
}

/**
 * Chapters the player can pick as a starting point. Excludes chapter
 * 0 (backstory, auto-played), chapter 1 (no prior decisions to
 * restore — equivalent to New Game) and the 2 latest chapters so
 * the player always has content to play through after resuming.
 */
export function getPickableChapters(allStoryflows) {
  if (!allStoryflows?.length) return [];
  const sorted = [...allStoryflows]
    .filter((s) => (s.number ?? null) !== null && (s.number ?? 0) >= 2)
    .sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
  if (sorted.length <= 2) return [];
  return sorted.slice(0, sorted.length - 2);
}
